/**
 * Pairs Auto-Close Bot
 * Monitors open pair positions and auto-closes based on z-score
 */

class PairsAutoCloseBot {
    constructor() {
        this.openPositions = new Map(); // executionId -> PairPosition
        this.isRunning = false;
        this.checkInterval = null;
        this.CHECK_INTERVAL_MS = 5000; // Check every 5 seconds
        
        // Cross-tab coordination
        this.mode = 'inactive'; // 'leader', 'follower', 'inactive'
        // console.log('[PAIRS-BOT] ✅ Initialized');
    }

    /**
     * Start the auto-close bot
     */
    start() {
        if (this.isRunning) return;

        this.isRunning = true;
        this.checkInterval = setInterval(() => this.checkExits(), this.CHECK_INTERVAL_MS);
        // console.log('[PAIRS-BOT] Started auto-close monitoring');
    }

    /**
     * Stop the auto-close bot
     */
    stop() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
        this.isRunning = false;
        // console.log('[PAIRS-BOT] Stopped auto-close monitoring');
    }

    /**
     * Register an open pair position
     * @param {Object} position - Position data
     */
    registerPosition(position) {
        const {
            executionId, symbolA, symbolB, directionA, directionB,
            entryZScore, entryTime, sizeA, sizeB, halfLife,
            entryPriceA, entryPriceB
        } = position;

        this.openPositions.set(executionId, {
            symbolA, symbolB, directionA, directionB,
            entryZScore: entryZScore || 0,
            entryTime: entryTime || Date.now(),
            sizeA, sizeB,
            entryPriceA, entryPriceB,
            halfLife: halfLife || 60,
            // Exit conditions
            tpZScore: 0.5,  // TP when z-score returns to near mean (±0.5σ)
            slZScore: Math.abs(entryZScore || 2.0) + 1.5,
            timeStopBars: (halfLife || 60) * 2,
            maxSingleAssetMove: 0.15
        });

        // console.log(`[PAIRS-BOT] Registered position ${executionId}: ${directionA} ${symbolA} / ${directionB} ${symbolB}`);

        // Auto-start if not running
        if (!this.isRunning && this.openPositions.size > 0) {
            this.start();
        }
    }

    /**
     * Check all positions for exit conditions
     */
    async checkExits() {
        // LEADER ONLY: Prevent execution in follower tabs
        if (this.mode !== 'leader') {
            return;
        }
        
        if (this.openPositions.size === 0) {
            this.stop();
            return;
        }

        for (const [executionId, pos] of this.openPositions) {
            try {
                const exitReason = await this.shouldExit(pos);

                if (exitReason) {
                    // console.log(`[PAIRS-BOT] Exit triggered for ${executionId}: ${exitReason}`);
                    await this.closePosition(executionId, exitReason);
                }
            } catch (error) {
                console.error(`[PAIRS-BOT] Error checking ${executionId}:`, error);
            }
        }
    }

    /**
     * Check if position should exit
     * @param {Object} pos - Position data
     * @returns {string|null} Exit reason or null to hold
     */
    async shouldExit(pos) {
        // Get current z-score from tracker service
        const currentZScore = await this.getCurrentZScore(pos.symbolA, pos.symbolB);

        if (currentZScore === null) return null;

        const barsHeld = (Date.now() - pos.entryTime) / (60 * 1000); // In minutes

        // 1. Take Profit: Z-score returned to mean
        if (Math.abs(currentZScore) <= pos.tpZScore) {
            return 'TAKE_PROFIT';
        }

        // 2. Stop Loss: Spread widened further (same direction as entry)
        const sameDirection = (currentZScore > 0) === (pos.entryZScore > 0);
        if (sameDirection && Math.abs(currentZScore) >= pos.slZScore) {
            return 'STOP_LOSS';
        }

        // 3. Time Stop: Held too long without reversion
        if (barsHeld >= pos.timeStopBars) {
            return 'TIME_STOP';
        }

        // 4. Correlation Break: Large single asset move
        const moveCheck = await this.checkCorrelationBreak(pos);
        if (moveCheck) {
            return 'CORRELATION_BREAK';
        }

        return null; // Hold position
    }

    /**
     * Check for correlation breakdown (large single asset move)
     */
    async checkCorrelationBreak(pos) {
        if (!pos.entryPriceA || !pos.entryPriceB) return false;

        const currentPriceA = await this.getPrice(pos.symbolA);
        const currentPriceB = await this.getPrice(pos.symbolB);

        if (!currentPriceA || !currentPriceB) return false;

        const moveA = Math.abs((currentPriceA - pos.entryPriceA) / pos.entryPriceA);
        const moveB = Math.abs((currentPriceB - pos.entryPriceB) / pos.entryPriceB);

        return moveA > pos.maxSingleAssetMove || moveB > pos.maxSingleAssetMove;
    }

    /**
     * Get current z-score for a pair
     */
    async getCurrentZScore(symbolA, symbolB) {
        // Try BrainService first (has Kalman pairs data)
        if (window.BrainService?.getKalmanPairs) {
            try {
                const allPairs = await window.BrainService.getKalmanPairs();
                const pair = allPairs?.find(p =>
                    (p.symbol_a === symbolA && p.symbol_b === symbolB) ||
                    (p.symbol_a === symbolB && p.symbol_b === symbolA) ||
                    p.pair === `${symbolA}/${symbolB}` ||
                    p.pair === `${symbolB}/${symbolA}`
                );
                if (pair?.zscore !== undefined) {
                    return pair.zscore;
                }
            } catch (e) {
                // Fallback to tracker service
            }
        }

        // Fallback: Try tracker service
        if (window.trackerService?.getSymbolData) {
            try {
                const data = await window.trackerService.getSymbolData(symbolA);
                const pairs = data?.kalman_pairs?.pairs || [];
                const pair = pairs.find(p =>
                    p.pair === `${symbolA}/${symbolB}` ||
                    p.pair === `${symbolB}/${symbolA}`
                );
                return pair?.zscore || null;
            } catch (e) {
                // No data available
            }
        }

        return null;
    }

    /**
     * Get current price for a symbol
     */
    async getPrice(symbol) {
        // Use price service first (WebSocket - fastest)
        if (window.BrowserPriceService?.isReady?.() && window.BrowserPriceService.getPrice) {
            const price = window.BrowserPriceService.getPrice(symbol);
            if (price) return price;
        }

        // Fallback to Hyperliquid client
        if (window.BrowserHyperliquidClient) {
            const mids = await window.BrowserHyperliquidClient.getAllMids();
            return mids?.[symbol] || null;
        }

        return null;
    }

    /**
     * Close a pair position
     */
    async closePosition(executionId, reason) {
        const pos = this.openPositions.get(executionId);
        if (!pos) return;

        // console.log(`[PAIRS-BOT] Closing ${executionId} (${reason})`);

        try {
            // Close both legs
            if (window.BrowserHyperliquidClient) {
                await Promise.all([
                    window.BrowserHyperliquidClient.closePosition(pos.symbolA).catch(e => {
                        console.error(`[PAIRS-BOT] Error closing ${pos.symbolA}:`, e);
                    }),
                    window.BrowserHyperliquidClient.closePosition(pos.symbolB).catch(e => {
                        console.error(`[PAIRS-BOT] Error closing ${pos.symbolB}:`, e);
                    })
                ]);
            }

            this.openPositions.delete(executionId);
            // console.log(`[PAIRS-BOT] Closed ${executionId} successfully`);

            // Emit event for UI update
            if (window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('pairs-position-closed', {
                    detail: { executionId, reason, position: pos }
                }));
            }

        } catch (error) {
            console.error(`[PAIRS-BOT] Error closing ${executionId}:`, error);
        }
    }

    /**
     * Manually close a position
     */
    async manualClose(executionId) {
        return this.closePosition(executionId, 'MANUAL');
    }

    /**
     * Get all open positions
     */
    getOpenPositions() {
        return Array.from(this.openPositions.entries()).map(([id, pos]) => ({
            executionId: id,
            ...pos,
            holdTime: Date.now() - pos.entryTime
        }));
    }

    /**
     * Get position count
     */
    getPositionCount() {
        return this.openPositions.size;
    }

    /**
     * Check if a pair is already open
     */
    hasPairPosition(symbolA, symbolB) {
        for (const pos of this.openPositions.values()) {
            if ((pos.symbolA === symbolA && pos.symbolB === symbolB) ||
                (pos.symbolA === symbolB && pos.symbolB === symbolA)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Set mode (for TabCoordinator)
     * @param {string} newMode - 'leader' or 'follower'
     */
    async setMode(newMode) {
        console.log(`[PAIRS-BOT] Switching mode: ${this.mode} -> ${newMode}`);
        this.mode = newMode;
    }
}

// Export to window for V4 browser services
if (typeof window !== 'undefined') {
    window.PairsAutoCloseBot = new PairsAutoCloseBot();
}
